// The app's one chat store, above the routes (GRO-274).
//
// A turn belongs to the store, not to the panel that started it: leave the Scout tab for the Board
// and the answer keeps streaming into the store, and the Scout nav shows a dot when it lands. The
// Scout tab, the account sidebar and the admin console all read their thread from here.

import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { readViewAs } from '../api/client';
import { createChatTransport } from '../api/chatTransport';
import { useAuthContext } from '../auth/AuthContext';
import { chatActorEmail } from '../lib/chatThread.ts';
import { closeStore, createChatStore, refreshConversations, subscribeStore, threadView, unreadConversationCount } from '../lib/chatStore.ts';
import type { ChatStore } from '../lib/chatStore.ts';
import type { ThreadView } from '../lib/chatFeed.ts';

const ChatStoreContext = createContext<ChatStore | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const { getToken, email } = useAuthContext();
  const queryClient = useQueryClient();
  const actor = chatActorEmail(email, readViewAs());

  // One store per acting person: a view-as switch is a different person's chats.
  const storeRef = useRef<{ actor: string; store: ChatStore } | null>(null);
  if (storeRef.current === null || storeRef.current.actor !== actor) {
    if (storeRef.current !== null) closeStore(storeRef.current.store);
    storeRef.current = { actor, store: createChatStore(createChatTransport(getToken)) };
  }
  const store = storeRef.current.store;

  // Scout may have saved contacts, queued a request or assigned a list: refetch what mirrors those.
  store.onTurnEnded = () => {
    void queryClient.invalidateQueries({ queryKey: ['book'] });
    void queryClient.invalidateQueries({ queryKey: ['my-requests'] });
    void queryClient.invalidateQueries({ queryKey: ['approval-queue'] });
  };

  // On sign-in, the chat list: unread chats get their dot, running ones are re-attached.
  useEffect(() => {
    if (!email) return;
    void refreshConversations(store, actor);
  }, [store, actor, email]);

  return <ChatStoreContext.Provider value={store}>{children}</ChatStoreContext.Provider>;
}

export function useChatStore(): ChatStore {
  const store = useContext(ChatStoreContext);
  if (store === null) throw new Error('useChatStore needs a ChatProvider above it.');
  return store;
}

/** Re-render on any store change. The store bumps `version` on each. */
export function useChatVersion(store: ChatStore): number {
  return useSyncExternalStore(
    listener => subscribeStore(store, listener),
    () => store.version,
  );
}

export function useThread(key: string): ThreadView {
  const store = useChatStore();
  useChatVersion(store);
  return threadView(store, key);
}

/** The dot on the Scout nav: Scout-tab chats that finished while the person was elsewhere. */
export function useUnreadChatCount(): number {
  const store = useContext(ChatStoreContext);
  const version = useSyncExternalStore(
    listener => (store ? subscribeStore(store, listener) : () => undefined),
    () => store?.version ?? 0,
  );
  return store && version >= 0 ? unreadConversationCount(store) : 0;
}
